import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import { X, UserPlus, Warning, Copy, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { InputField, SelectField, FormField } from '@/components/ui/form-field';
import { PhoneInput } from '@/components/ui/phone-input';
import { sanitizeName } from '@/lib/validators';
import { useFormValidation, rules } from '@/hooks/useFormValidation';
import {
  inviteUser,
  type InviteUserPayload,
  type InviteUserResult,
  type UserMemberType,
} from '@/lib/usersApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: (user: InviteUserResult['user']) => void;
  /** Pre-select the role dropdown (e.g. 'silk' from the Barristers page,
   *  'senior_clerk' from the Clerks page). Defaults to 'staff'. */
  defaultMemberType?: Exclude<UserMemberType, 'solicitor'>;
}

const ROLE_OPTIONS: { value: Exclude<UserMemberType, 'solicitor'>; label: string }[] = [
  { value: 'admin', label: 'Chambers admin' },
  { value: 'senior_clerk', label: 'Senior clerk' },
  { value: 'junior_clerk', label: 'Junior clerk' },
  { value: 'staff', label: 'Staff' },
  { value: 'silk', label: 'Silk (KC)' },
  { value: 'junior', label: 'Junior barrister' },
  { value: 'pupil', label: 'Pupil' },
  { value: 'door_tenant', label: 'Door tenant' },
];

export function InviteMemberModal({ open, onClose, onSuccess, defaultMemberType }: Props) {
  const initialRole: Exclude<UserMemberType, 'solicitor'> = defaultMemberType ?? 'staff';
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [memberType, setMemberType] =
    useState<Exclude<UserMemberType, 'solicitor'>>(initialRole);
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteUserResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Form values object for validation
  const formValues = useMemo(
    () => ({ email, firstName, lastName, memberType, jobTitle, phone }),
    [email, firstName, lastName, memberType, jobTitle, phone]
  );

  // Validation rules
  const { isValid, validateAll, resetValidation, getFieldProps } = useFormValidation(
    formValues,
    {
      validations: {
        firstName: [rules.required('First name'), rules.personName('First name')],
        lastName: [rules.required('Last name'), rules.personName('Last name')],
        email: [
          rules.required('Email'),
          rules.email('Email'),
        ],
        memberType: [rules.required('Role')],
        phone: [rules.phone('Phone')],
      },
    }
  );

  useEffect(() => {
    if (open) return;
    setEmail('');
    setFirstName('');
    setLastName('');
    setMemberType(initialRole);
    setJobTitle('');
    setPhone('');
    setApiError(null);
    setResult(null);
    setCopied(false);
    setCopiedLink(false);
    resetValidation();
  }, [open, resetValidation, initialRole]);

  if (!open) return null;

  const canSubmit = !submitting && !result && isValid;

  const handleSubmit = async () => {
    if (!validateAll()) return;
    if (submitting) return;

    setSubmitting(true);
    setApiError(null);
    try {
      const payload: InviteUserPayload = {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        memberType,
        ...(jobTitle.trim() && { jobTitle: jobTitle.trim() }),
        ...(phone.trim() && { phone: phone.trim() }),
      };
      const r = await inviteUser(payload);
      setResult(r);
      toast.success(`${r.user.firstName} invited`);
      onSuccess?.(r.user);
    } catch (err) {
      setApiError(getApiErrorMessage(err, 'Could not send the invite'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  const handleCopyLink = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.activationUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 sm:items-center">
      <ScrollLock />
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-brand-600 dark:text-brand-400" />
            <div className="text-sm font-semibold text-foreground">
              {result ? 'Member invited' : 'Invite member'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!result ? (
          <>
            <div className="space-y-4 px-5 py-4">
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="First name"
                  placeholder="Margaret"
                  maxLength={200}
                  value={firstName}
                  onChange={(e) => setFirstName(sanitizeName(e.target.value))}
                  autoFocus
                  required
                  {...getFieldProps('firstName')}
                />
                <InputField
                  label="Last name"
                  placeholder="Hughes"
                  maxLength={200}
                  value={lastName}
                  onChange={(e) => setLastName(sanitizeName(e.target.value))}
                  required
                  {...getFieldProps('lastName')}
                />
              </div>

              <InputField
                type="email"
                label="Email"
                placeholder="margaret@chambers.test"
                maxLength={255}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                {...getFieldProps('email')}
              />

              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  label="Role"
                  value={memberType}
                  onChange={(e) =>
                    setMemberType(e.target.value as Exclude<UserMemberType, 'solicitor'>)
                  }
                  required
                  {...getFieldProps('memberType')}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </SelectField>
                <FormField label="Phone" hint="Optional" error={getFieldProps('phone').error}>
                  <PhoneInput value={phone} onChange={setPhone} onBlur={getFieldProps('phone').onBlur} />
                </FormField>
              </div>

              <InputField
                label="Job title"
                placeholder="Senior Clerk"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                hint="Optional"
              />

              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Solicitors are invited from the matter detail page (different
                flow — magic-link auth, scoped to their firm).
              </div>

              {apiError && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
                  <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{apiError}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
              <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? 'Sending invite…' : 'Send invite'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-4 px-5 py-4">
              <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-900 dark:text-emerald-200">
                <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  <strong>{result.user.firstName} {result.user.lastName}</strong>{' '}
                  has been added. They will receive a sign-in email shortly.
                </span>
              </div>

              {/* Activation link — the primary way to onboard when email isn't delivering:
                  the invitee opens it and sets their own password. */}
              <div>
                <div className="block text-xs font-medium text-muted-foreground mb-1.5">
                  Activation link — send to {result.user.firstName} if the email doesn’t arrive
                </div>
                <div className="flex items-center gap-2 rounded-md border border-brand-300 bg-brand-50 dark:border-brand-500/40 dark:bg-brand-500/10 px-3 py-2">
                  <code className="flex-1 break-all text-xs text-brand-900 dark:text-brand-200">
                    {result.activationUrl}
                  </code>
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-1 rounded-md border border-brand-300 bg-white dark:border-brand-700/50 dark:bg-slate-800 px-2 py-1 text-xs font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-slate-700 whitespace-nowrap"
                  >
                    {copiedLink ? (<><CheckCircle className="h-3 w-3" />Copied</>) : (<><Copy className="h-3 w-3" />Copy</>)}
                  </button>
                  <a
                    href={result.activationUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-md border border-brand-300 bg-white dark:border-brand-700/50 dark:bg-slate-800 px-2 py-1 text-xs font-medium text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-slate-700 whitespace-nowrap"
                  >
                    Open
                  </a>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  They open this link and set their own password — works even if email delivery isn’t set up.
                </p>
              </div>

              <div>
                <div className="block text-xs font-medium text-muted-foreground mb-1.5">Or share a temporary password (out-of-band)</div>
                <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10 px-3 py-2">
                  <code className="flex-1 break-all text-sm text-amber-950 dark:text-amber-200">
                    {result.temporaryPassword}
                  </code>
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white dark:border-amber-700/50 dark:bg-slate-800 px-2 py-1 text-xs font-medium text-amber-900 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-500/20 dark:bg-amber-500/15 dark:hover:bg-slate-700"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-3 w-3" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  This password is shown <strong>once</strong>. Hand it to the new
                  member through a secure channel (in person, encrypted message).
                  They will be required to set their own password on first sign-in.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
              <Button variant="primary" size="sm" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
